/**
 * The collection half of the y-sort pass: it flattens a y_sort_enabled node's
 * subtree into the list `<YSortDispatcher>` sorts, as Godot's
 * `_collect_ysort_children` does. Pure data: no React, no THREE.
 */
import { isTopLevelItem } from './canvasPaintOrder.js';
import { accumulateCanvasItemZ } from './lighting2d/canvasItemPlacement.js';
import { nodeComponentRegistry } from './NodeComponentRegistry.js';
/**
 * An item's identity within one flat sort, for React keys and THREE object names.
 * Distinct for every item, so a duplicate node name cannot merge two.
 */
export function ySortItemId(item) {
    return item.groupIndex === undefined ? `${item.treeOrder}` : `${item.treeOrder}_${item.groupIndex}`;
}
/**
 * The node with its subtree removed. A y_sort_enabled node's children are separate
 * entries in the flat sort, so the node contributes only its own pixels, which
 * `CanvasItem2D` renders from `body`.
 */
function ownBodyOf(node) {
    return { ...node, children: [] };
}
/** Compute the sortY and effectiveZ for a single CanvasItem node relative to parent context. */
function itemSortKey(node, parent) {
    const props = node.properties;
    const localY = props.position?.y ?? 0;
    const ySortOrigin = props.y_sort_origin ?? 0;
    const sortY = parent.parentWorldY + localY + ySortOrigin;
    // The same accumulation the light cull uses: `_collect_ysort_children` (renderer_canvas_cull.cpp
    // 160-166) and `_cull_canvas_item` (816-820) both run
    // `CLAMP(p_z + z_index, CANVAS_ITEM_Z_MIN, CANVAS_ITEM_Z_MAX)` under `z_relative`
    // and assign `z_index` unclamped otherwise. A second copy could drift from the lights.
    const effectiveZ = accumulateCanvasItemZ(parent.parentEffectiveZ, {
        z_index: props.z_index ?? 0,
        z_as_relative: props.z_as_relative,
    });
    return { sortY, effectiveZ };
}
/**
 * Collect y-sorted items from a node's children: a y_sort_enabled TileMapLayer is a
 * tileGroup, another y_sort_enabled child is its own body and then its merged subtree,
 * and any other child is one atomic unit. `liftedPast` is the chain of y_sort_enabled
 * ancestors, so the renderer can restore their transforms and path segments.
 */
export function collectYSortedItems(node, parent, startOrder, liftedPast = []) {
    const items = [];
    let order = startOrder;
    for (const child of node.children) {
        // `_collect_ysort_children` walks the RenderingServer's `child_items`
        // (`renderer_canvas_cull.cpp:110-115`). A top_level item is parented at the
        // canvas instead (`canvas_item.cpp:234-285`), so it is never among them.
        // `<YSortDispatcher>` draws it as a canvas root.
        if (isTopLevelItem(child))
            continue;
        const key = itemSortKey(child, parent);
        const props = child.properties;
        const isYSort = props.y_sort_enabled === true;
        const group = isYSort ? nodeComponentRegistry.getYSortGroup(child.type) : undefined;
        if (group && nodeComponentRegistry.isCanvasItem(child.type)) {
            const layer = group.describe(child);
            items.push({
                sortY: key.sortY,
                effectiveZ: key.effectiveZ,
                treeOrder: order++,
                kind: 'tileGroup',
                tileData: {
                    tileSetRef: layer.tileSetRef,
                    worldY: parent.parentWorldY + layer.positionY,
                },
                node: child,
                liftedPast,
            });
            continue;
        }
        if (isYSort) {
            // Godot sorts a y_sort_enabled node alongside the subtree it merges in: the
            // node is appended first, then descended into. Without the append, the
            // node's own pixels are lost.
            items.push({
                sortY: key.sortY,
                effectiveZ: key.effectiveZ,
                treeOrder: order++,
                kind: 'node',
                node: ownBodyOf(child),
                liftedPast,
            });
            // Its descendants sort against the child's accumulated position and z-index
            // bucket, not the sort root's: Godot threads `ysort_xform` and `abs_z` through.
            const childLocalY = props.position?.y ?? 0;
            const subItems = collectYSortedItems(child, { parentWorldY: parent.parentWorldY + childLocalY, parentEffectiveZ: key.effectiveZ }, order, [...liftedPast, child]);
            items.push(...subItems);
            order += subItems.length;
        }
        else {
            items.push({
                sortY: key.sortY,
                effectiveZ: key.effectiveZ,
                treeOrder: order++,
                kind: 'node',
                node: child,
                liftedPast,
            });
        }
    }
    return items;
}
//# sourceMappingURL=ySortItems.js.map